import styled from "styled-components";

// Theme constants
export const THEME = {
  colors: {
    primary: "#c3af83",
    primaryDark: "#561f2f",
    primaryLight: "#f5f0f1",
    border: "#808080",
    borderLight: "#ccc",
    borderDark: "#999",
    error: "#ff4d4f",
    errorBg: "#fee",
    errorBorder: "#fcc",
    success: "#09AF06",
    successBg: "#d4edda",
    successBorder: "#c3e6cb",
    info: "#0066cc",
    infoBg: "#f8f9fa",
    infoBorder: "#e9ecef",
    infoLight: "#f0f8ff",
    infoLightBorder: "#cce7ff",
    warning: "#f59e0b",
    warningBg: "#fef3c7",
    warningBorder: "#fcd34d",
    text: "#000000",
    textMuted: "#666666",
    textSecondary: "#333333",
    textLight: "#ffffff",
    white: "#ffffff",
    background: "#f0f0f0",
    backgroundLight: "#f9f9f9",
    backgroundGray: "#ddd",
    selectedBg: "#d4b886",
  },
  spacing: {
    xs: "4px",
    sm: "8px",
    md: "12px",
    lg: "16px",
    xl: "20px",
    xxl: "24px",
    xxxl: "32px",
    giant: "36px",
    mega: "40px",
    ultra: "60px",
  },
  borderRadius: {
    sm: "4px",
    md: "8px",
  },
  shadows: {
    dropdown: "0 2px 8px rgba(0, 0, 0, 0.1)",
  },
  transitions: {
    default: "all 0.2s ease-in-out",
    fast: "all 0.15s ease-in-out",
    slow: "all 0.2s ease",
  },
  fontSize: {
    xs: "12px",
    sm: "14px",
    md: "16px",
    lg: "18px",
    xl: "20px",
  },
};

// Configurable Card Container
export const OnboardingCard = styled.div.withConfig({
  shouldForwardProp: (prop) =>
    ![
      "borderRadius",
      "padding",
      "direction",
      "maxWidth",
      "width",
      "alignItems",
      "justifyContent",
      "gap",
    ].includes(prop),
})`
  border: 1px solid ${THEME.colors.border};
  border-radius: ${({ borderRadius = THEME.borderRadius.md }) => borderRadius};
  padding: ${({ padding = THEME.spacing.lg }) => padding};
  display: flex;
  flex-direction: ${({ direction = "column" }) => direction};
  font-family: sans-serif;
  max-width: ${({ maxWidth }) => maxWidth || "none"};
  width: ${({ width = "100%" }) => width};
  align-items: ${({ alignItems }) => alignItems || "stretch"};
  justify-content: ${({ justifyContent }) => justifyContent || "flex-start"};
  gap: ${({ gap }) => gap || "0"};
`;

// Responsive Form Grid
export const FormGrid = styled.div.withConfig({
  shouldForwardProp: (prop) =>
    ![
      "columnGap",
      "rowGap",
      "columns",
      "marginTop",
      "marginBottom",
      "width",
      "breakpoint",
      "mobileGap",
    ].includes(prop),
})`
  display: grid;
  grid-template-columns: ${({ columns = "1fr 1fr" }) => columns};
  column-gap: ${({ columnGap = "60px" }) => columnGap}; /* Fixed from 69px */
  row-gap: ${({ rowGap = "32px" }) =>
    rowGap}; /* Fixed from 29px to multiple of 4 */
  margin-top: ${({ marginTop = THEME.spacing.xl }) => marginTop};
  margin-bottom: ${({ marginBottom = THEME.spacing.xl }) => marginBottom};
  width: ${({ width = "100%" }) => width};

  @media (max-width: ${({ breakpoint = "600px" }) => breakpoint}) {
    display: flex;
    flex-direction: column;
    gap: ${({ mobileGap = "20px" }) => mobileGap};
  }
`;

// Flex Layout Utilities
export const FlexColumn = styled.div.withConfig({
  shouldForwardProp: (prop) =>
    ![
      "gap",
      "alignItems",
      "justifyContent",
      "width",
      "maxWidth",
      "padding",
      "margin",
    ].includes(prop),
})`
  display: flex;
  flex-direction: column;
  gap: ${({ gap = THEME.spacing.md }) => gap};
  align-items: ${({ alignItems = "stretch" }) => alignItems};
  justify-content: ${({ justifyContent = "flex-start" }) => justifyContent};
  width: ${({ width = "100%" }) => width};
  max-width: ${({ maxWidth }) => maxWidth || "none"};
  padding: ${({ padding = "0" }) => padding};
  margin: ${({ margin = "0" }) => margin};
`;

export const FlexRow = styled.div.withConfig({
  shouldForwardProp: (prop) =>
    ![
      "gap",
      "alignItems",
      "justifyContent",
      "width",
      "wrap",
      "padding",
      "margin",
    ].includes(prop),
})`
  display: flex;
  flex-direction: row;
  gap: ${({ gap = THEME.spacing.md }) => gap};
  align-items: ${({ alignItems = "center" }) => alignItems};
  justify-content: ${({ justifyContent = "flex-start" }) => justifyContent};
  width: ${({ width = "100%" }) => width};
  flex-wrap: ${({ wrap = "nowrap" }) => wrap};
  padding: ${({ padding = "0" }) => padding};
  margin: ${({ margin = "0" }) => margin};
`;

// Message Box with variants
export const MessageBox = styled.div.withConfig({
  shouldForwardProp: (prop) =>
    !["padding", "borderRadius", "margin", "variant", "customStyles"].includes(
      prop
    ),
})`
  padding: ${({ padding = THEME.spacing.md }) => padding};
  border-radius: ${({ borderRadius = THEME.borderRadius.sm }) => borderRadius};
  margin: ${({ margin = `${THEME.spacing.md} 0` }) => margin};
  font-family: sans-serif;
  transition: ${THEME.transitions.default};

  ${({ variant = "info" }) => {
    switch (variant) {
      case "error":
        return `
          color: ${THEME.colors.error};
          background-color: ${THEME.colors.errorBg};
          border: 1px solid ${THEME.colors.errorBorder};
        `;
      case "success":
        return `
          color: ${THEME.colors.success};
          background-color: ${THEME.colors.successBg};
          border: 1px solid ${THEME.colors.successBorder};
        `;
      case "warning":
        return `
          color: ${THEME.colors.warning};
          background-color: ${THEME.colors.warningBg};
          border: 1px solid ${THEME.colors.warningBorder};
        `;
      case "info":
      default:
        return `
          color: ${THEME.colors.info};
          background-color: ${THEME.colors.infoBg};
          border: 1px solid ${THEME.colors.infoBorder};
        `;
    }
  }}

  /* Allow custom styles to override */
  ${({ customStyles }) => customStyles || ""}
`;

// Completed Section Component
export const CompletedSection = styled.div.withConfig({
  shouldForwardProp: (prop) =>
    ![
      "alignItems",
      "gap",
      "padding",
      "backgroundColor",
      "borderColor",
      "borderRadius",
      "margin",
    ].includes(prop),
})`
  display: flex;
  align-items: ${({ alignItems = "flex-start" }) => alignItems};
  gap: ${({ gap = THEME.spacing.md }) => gap};
  padding: ${({ padding = THEME.spacing.lg }) => padding};
  background-color: ${({ backgroundColor = THEME.colors.successBg }) =>
    backgroundColor};
  border: 1px solid
    ${({ borderColor = THEME.colors.successBorder }) => borderColor};
  border-radius: ${({ borderRadius = THEME.borderRadius.md }) => borderRadius};
  margin: ${({ margin = `${THEME.spacing.md} 0` }) => margin};
  transition: ${THEME.transitions.default};
`;

// Address Container (from OnboardingGetCompanyInfo)
export const AddressContainer = styled.div.withConfig({
  shouldForwardProp: (prop) => !["padding", "gap"].includes(prop),
})`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: ${({ padding = "21px 0 14px 0" }) => padding};
  gap: ${({ gap = THEME.spacing.sm }) => gap};
`;

// Address Line with Two Values (from OnboardingGetCompanyInfo)
export const AddressLineWithTwoValues = styled.div.withConfig({
  shouldForwardProp: (prop) => !["gap"].includes(prop),
})`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${({ gap = THEME.spacing.xs }) => gap};
  width: 100%;
`;

// Search Container (from OnboardingGetCompanyInfo)
export const SearchContainer = styled.div.withConfig({
  shouldForwardProp: (prop) => !["width", "maxWidth"].includes(prop),
})`
  position: relative;
  width: ${({ width = "100%" }) => width};
  max-width: ${({ maxWidth }) => maxWidth || "none"};
`;

// Dropdown Container (from OnboardingGetCompanyInfo)
export const DropdownContainer = styled.div.withConfig({
  shouldForwardProp: (prop) => !["zIndex", "maxHeight"].includes(prop),
})`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid ${THEME.colors.border};
  border-top: none;
  border-radius: 0 0 ${THEME.borderRadius.sm} ${THEME.borderRadius.sm};
  box-shadow: ${THEME.shadows.dropdown};
  z-index: ${({ zIndex = "1000" }) => zIndex};
  max-height: ${({ maxHeight = "300px" }) => maxHeight};
  overflow-y: auto;
`;

// Dropdown Item (from OnboardingGetCompanyInfo)
export const DropdownItem = styled.div.withConfig({
  shouldForwardProp: (prop) =>
    !["padding", "gap", "hoverColor", "highlightColor"].includes(prop),
})`
  padding: ${({ padding = `${THEME.spacing.sm} ${THEME.spacing.md}` }) =>
    padding};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${({ gap = "12px" }) => gap};
  transition: ${THEME.transitions.fast};

  &:hover {
    background-color: ${({ hoverColor = "#f8f9fa" }) => hoverColor};
  }

  &.highlighted {
    background-color: ${({ highlightColor = "#f6ff7f" }) => highlightColor};
  }
`;

// Empty Grid Block (responsive helper)
export const EmptyGridBlock = styled.div.withConfig({
  shouldForwardProp: (prop) => !["breakpoint"].includes(prop),
})`
  @media (max-width: ${({ breakpoint = "600px" }) => breakpoint}) {
    display: none;
  }
`;

// Icon Container (reusable for various icons)
export const IconContainer = styled.div.withConfig({
  shouldForwardProp: (prop) => !["size", "color"].includes(prop),
})`
  width: ${({ size = "24px" }) => size};
  height: ${({ size = "24px" }) => size};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${({ color = THEME.colors.textMuted }) => color};
`;

// Content Container (for complex nested content)
export const ContentContainer = styled.div.withConfig({
  shouldForwardProp: (prop) => !["gap"].includes(prop),
})`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${({ gap = THEME.spacing.xs }) => gap};
  min-width: 0; /* Prevent flex item overflow */
`;

// Centered container for completion states
export const CenteredContainer = styled.div.withConfig({
  shouldForwardProp: (prop) => !["padding", "margin"].includes(prop),
})`
  text-align: center;
  padding: ${({ padding = THEME.spacing.mega }) => padding};
  margin: ${({ margin = "0" }) => margin};
`;

// Button section container
export const ButtonSection = styled.div.withConfig({
  shouldForwardProp: (prop) => !["marginTop", "gap"].includes(prop),
})`
  margin-top: ${({ marginTop = THEME.spacing.xxxl }) => marginTop};
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ gap = THEME.spacing.sm }) => gap};
`;

// Disabled button styling component
export const DisabledButtonState = styled.div.withConfig({
  shouldForwardProp: (prop) => !["disabled"].includes(prop),
})`
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
`;

// Responsive field containers
export const ResponsiveFieldSmall = styled.div`
  flex: 1 1 200px;
  min-width: 150px;
`;

export const ResponsiveFieldMedium = styled.div`
  flex: 1 1 250px;
  min-width: 200px;
`;

export const ResponsiveFieldLarge = styled.div`
  flex: 1 1 300px;
  min-width: 250px;
`;

// Spinner component for loading states
export const Spinner = styled.div.withConfig({
  shouldForwardProp: (prop) => !["size", "color", "marginRight"].includes(prop),
})`
  width: ${({ size = "16px" }) => size};
  height: ${({ size = "16px" }) => size};
  border: 2px solid transparent;
  border-top: 2px solid ${({ color = THEME.colors.text }) => color};
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-right: ${({ marginRight = THEME.spacing.sm }) => marginRight};

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

// Button content wrapper for spinner + text
export const ButtonContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;
